// timeAgo — compact relative-time formatter ("just now", "5 mins ago",
// "3 hours ago", "2 days ago", "3 weeks ago", "4 months ago", "2 years
// ago"). ALWAYS relative — the old past-a-week raw-date fallback read as
// noise on ticket cards (2026-07-25 per Nika "instead of dates use 'day
// ago'"). The house relative-time helper (per Nika 2026-06-12, ticket
// card Updated) — registered on shared so any component can
// `el.call('timeAgo', ts)`.
//
// IT FOLLOWS THE SESSION LANGUAGE. Its sentences were English literals, so a
// Georgian page read `2 weeks ago` on every ticket card and every note
// (measured live, 2026-09-20).
//   • English stays BYTE-IDENTICAL — `5 mins ago` is the wording that was
//     asked for, singular and plural by hand.
//   • Every other language reads the BUNDLE first (`common.time.*`, one
//     `{count}` sentence per unit). Georgian keeps the noun in one form after
//     any number (`1 წუთის წინ`, `5 წუთის წინ`), so one sentence per unit is
//     exact for it.
//   • `Intl.RelativeTimeFormat` is only the fallback for a language with no
//     bundle sentences, and only when the BROWSER knows the language.
// ⛔ Intl alone was the first version of this fix, and it was WRONG in the
// product: Chrome ships no Georgian locale data —
// `Intl.RelativeTimeFormat.supportedLocalesOf(['ka'])` answers `[]` and the
// formatter silently resolves to `en-GB`. Node and bun carry full ICU, so the
// unit test read perfect Georgian while the running board still said
// `2 weeks ago`. The live check caught it; the test simulates that browser now.
//
// The language is read off the ELEMENT (`this`, the `el.call` binding). With no
// element, or a host with no polyglot, it answers English and never throws.
//
// FA204 — self-contained: every helper value lives inside the function.
export const timeAgo = function (input) {
  if (!input) return ''
  const then = new Date(input).getTime()
  if (!Number.isFinite(then)) return ''

  const canCall = this && typeof this.call === 'function'
  const lang = (canCall && this.call('getActiveLang')) || 'en'
  const english = lang === 'en'

  // Literal keys, never concatenated (docs/I18N-CONVENTION.md §1.3).
  const KEYS = {
    minute: 'common.time.minutesAgo',
    hour: 'common.time.hoursAgo',
    day: 'common.time.daysAgo',
    week: 'common.time.weeksAgo',
    month: 'common.time.monthsAgo',
    year: 'common.time.yearsAgo'
  }
  // `translate` answers the key itself for a string no bundle carries.
  const fromBundle = (key) => {
    if (!canCall) return null
    const word = this.call('polyglot', key)
    return typeof word === 'string' && word && word !== key ? word : null
  }
  const justNow = () => (english ? null : fromBundle('common.time.justNow')) || 'just now'
  // `unit` is the Intl unit; `one` / `many` are the English words.
  const say = (count, unit, one, many) => {
    const inEnglish = count === 1 ? `1 ${one} ago` : `${count} ${many} ago`
    if (english) return inEnglish
    const sentence = fromBundle(KEYS[unit])
    if (sentence) return sentence.replace('{count}', count)
    try {
      const Rtf = typeof Intl !== 'undefined' && Intl.RelativeTimeFormat
      if (Rtf && Rtf.supportedLocalesOf([lang]).length)
        return new Rtf(lang, { numeric: 'always' }).format(-count, unit)
    } catch (_) {}
    return inEnglish
  }

  const diff = Date.now() - then
  if (diff < 0) return justNow()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return justNow()
  if (mins < 60) return say(mins, 'minute', 'min', 'mins')
  const hours = Math.floor(mins / 60)
  if (hours < 24) return say(hours, 'hour', 'hour', 'hours')
  const days = Math.floor(hours / 24)
  if (days < 7) return say(days, 'day', 'day', 'days')
  const weeks = Math.floor(days / 7)
  if (days < 31) return say(weeks, 'week', 'week', 'weeks')
  const months = Math.floor(days / 30.44)
  if (months < 12) return say(Math.max(months, 1), 'month', 'month', 'months')
  const years = Math.floor(days / 365.25)
  return say(Math.max(years, 1), 'year', 'year', 'years')
}
